import React from 'react';

import { formatters } from 'services';
import { ordinaryQuoteIsAvailable } from '../MarketsTabV3/model';
import type { MarketInstrument } from '../MarketsTabV3/types';

type Props = {
  row?: MarketInstrument;
  reportingCurrencySymbol: string;
  showAvgOpenPrice?: boolean;
};

const value = (input: number | string | null | undefined, digits = 2) =>
  input == null
    ? '—'
    : digits === 0
      ? formatters.commasNoDigits(input)
      : formatters.commas2Digits(input);

const quoteValue = (row: MarketInstrument, side: 'bid' | 'ask') =>
  formatters.priceFormat(
    row.asset[side],
    row.asset.ticker,
    row.currencySymbol,
    row.asset.resourcetype,
  );

const Detail: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div>
    <dt>{label}</dt>
    <dd>{children}</dd>
  </div>
);

const SelectedInstrumentDetails: React.FC<Props> = ({
  row,
  reportingCurrencySymbol,
  showAvgOpenPrice = false,
}) => {
  if (!row) return null;

  const { asset, instrument } = row;
  const option = instrument.simplified_option;
  const quoted = ordinaryQuoteIsAvailable(asset);

  return (
    <section className="markets-v4-details" aria-label="Instrument details">
      <header>
        <span>Details</span>
      </header>

      <dl className="markets-v4-details__grid">
        {option ? (
          <>
            <Detail label="Strike">{value(option.current_strike)}</Detail>
            <Detail label="Annualized IV">
              {option.current_annualized_implied_volatility == null
                ? '—'
                : formatters.percent2Digit(
                    option.current_annualized_implied_volatility,
                  )}
            </Detail>
            <Detail label="Expiry">
              {option.days_to_expiry == null
                ? '—'
                : `${option.days_to_expiry} days`}
            </Detail>
            <Detail label="Premium / unit">
              {value(option.premium_per_controlled_unit)} {row.currencySymbol}
            </Detail>
            <Detail label="Contracts">{value(option.contracts, 0)}</Detail>
            <Detail label="Exposure">
              {value(
                option.reporting_controlled_exposure ??
                  option.controlled_exposure,
                0,
              )}{' '}
              {reportingCurrencySymbol}
            </Detail>
          </>
        ) : (
          <>
            <Detail label="Bid">{quoted ? quoteValue(row, 'bid') : '—'}</Detail>
            <Detail label="Ask">{quoted ? quoteValue(row, 'ask') : '—'}</Detail>
            {showAvgOpenPrice && (
              <Detail label="Avg. open">
                {formatters.priceFormat(
                  instrument.avg_open_price,
                  asset.ticker,
                  row.currencySymbol,
                  asset.resourcetype,
                )}
              </Detail>
            )}
            <Detail label="Position">{value(instrument.amount, 0)}</Detail>
            <Detail label="Market value">
              {value(row.reportingValue, 0)} {reportingCurrencySymbol}
            </Detail>
            {asset.resourcetype === 'AFSBond' && (
              <>
                <Detail label="Yield">
                  {formatters.commasExact3Digits(asset.ask_yield)}%
                </Detail>
                <Detail label="Coupon">{value(asset.coupon)}%</Detail>
                <Detail label="Duration">
                  {value(instrument.htm_afs_duration)}
                </Detail>
                <Detail label="DV01/1M">
                  {value(instrument.htm_afs_dv01)}
                </Detail>
                <Detail label="Maturity">
                  {value(instrument.htm_afs_years_to_maturity)} yrs
                </Detail>
              </>
            )}
            {asset.resourcetype.startsWith('ETF') && (
              <Detail label="Geography">{asset.geography || 'Global'}</Detail>
            )}
          </>
        )}
        <Detail label="Unrealized P&amp;L">
          {value(instrument.reporting_unrealized_pnl, 0)}{' '}
          {reportingCurrencySymbol}
        </Detail>
        <Detail label="Realized P&amp;L">
          {value(instrument.reporting_realized_pnl, 0)}{' '}
          {reportingCurrencySymbol}
        </Detail>
      </dl>
    </section>
  );
};

export default SelectedInstrumentDetails;
